import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchRecords } from '../services/api';

export const generateBankPaymentPDF = async (fileRecord) => {
  try {
    const fileId = fileRecord.file_id || fileRecord.id;
    if (!fileId) throw new Error('Bank Payment File ID missing.');

    // 1. Fetch Related Data
    const [
      payReq,
      runReq,
      asnReq,
      perReq,
      ppmReq,
      bgReq,
      ldgReq,
      compReq,
      bankReq
    ] = await Promise.all([
      fetchRecords('PAYMENT').catch(() => ({ data: { data: [] } })),
      fetchRecords('PAYROLL_RUN').catch(() => ({ data: { data: [] } })),
      fetchRecords('ASSIGNMENT').catch(() => ({ data: { data: [] } })),
      fetchRecords('PERSON').catch(() => ({ data: { data: [] } })),
      fetchRecords('PERSONAL_PAYMENT_METHOD').catch(() => ({ data: { data: [] } })),
      fetchRecords('BUSINESS_GROUP').catch(() => ({ data: { data: [] } })),
      fetchRecords('LEGISLATION_DATA_GROUP').catch(() => ({ data: { data: [] } })),
      fetchRecords('COMPANY').catch(() => ({ data: { data: [] } })),
      fetchRecords('BANK_MASTER').catch(() => ({ data: { data: [] } }))
    ]);

    // 2. Hydrate Context
    const run = (runReq.data?.data || []).find(r => r.run_id === fileRecord.run_id) || {};
    const linkedPayments = (payReq.data?.data || []).filter(p => p.run_id === fileRecord.run_id);
    
    if (linkedPayments.length === 0) {
      throw new Error('No linked payments found for this file.');
    }

    const assignmentMap = Object.fromEntries((asnReq.data?.data || []).map(a => [a.assignment_id, a]));
    const personMap = Object.fromEntries((perReq.data?.data || []).map(p => [p.person_id, p]));
    const ppmMap = Object.fromEntries((ppmReq.data?.data || []).map(p => [p.assignment_id, p]));
    
    // Org Info
    // Get Company from first assignment or from run
    let company = null;
    let bg = null;
    let ldg = null;
    if (linkedPayments.length > 0) {
      const firstAsn = assignmentMap[linkedPayments[0].assignment_id];
      if (firstAsn) {
        company = (compReq.data?.data || []).find(c => c.company_id === firstAsn.COMPANY_id);
        bg = (bgReq.data?.data || []).find(b => b.bg_id === firstAsn.bg_id);
        ldg = (ldgReq.data?.data || []).find(l => l.ldg_id === firstAsn.ldg_id);
      }
    }

    // 3. Format Data
    const fmtINR = (n) => 'Rs. ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    
    // Metrics
    let totalPending = 0;
    let totalFailed = 0;
    let totalAmount = 0;
    const methodCounts = {};

    const tableBody = linkedPayments.map((p, idx) => {
      const asn = assignmentMap[p.assignment_id] || {};
      const per = personMap[asn.person_id] || {};
      const ppm = ppmMap[p.assignment_id] || {};
      
      const empName = `${per.first_name || ''} ${per.last_name || ''}`.trim() || asn.employee_code || 'Unknown';
      const bankAccount = ppm.bank_account_id || 'N/A';
      const ifsc = ppm.ifsc_code || 'N/A';
      const customerId = ppm.customer_id || 'N/A';
      const method = ppm.payment_type || p.payment_method || 'BANK_TRANSFER';
      const status = p.payment_status || 'Pending';
      const amount = Number(p.net_amount || 0);

      if (status.toLowerCase() === 'pending') totalPending++;
      else if (status.toLowerCase() === 'failed' || status.toLowerCase() === 'error') totalFailed++;
      
      totalAmount += amount;
      methodCounts[method] = (methodCounts[method] || 0) + 1;

      return [
        (idx + 1).toString(),
        empName,
        asn.assignment_id || 'N/A',
        customerId,
        bankAccount,
        ifsc,
        method,
        fmtINR(amount),
        status,
        p.transaction_ref || 'N/A'
      ];
    });

    const methodBreakdown = Object.entries(methodCounts).map(([m, c]) => `${m}: ${c}`).join(' | ');

    // 4. Generate PDF
    const doc = new jsPDF('p', 'pt', 'a4');
    const pageWidth = doc.internal.pageSize.width;
    
    // --- Header Section ---
    doc.setFillColor(30, 64, 175); // Dark blue header
    doc.rect(0, 0, pageWidth, 100, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(company?.company_name || 'Your Company Pvt Ltd', 30, 40);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Business Group: ${bg?.bg_name || 'N/A'}`, 30, 60);
    doc.text(`Legislation: ${ldg?.legislation_name || 'N/A'}`, 30, 75);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('PAYSLIP DATA', pageWidth - 30, 40, { align: 'right' });
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`File: ${fileRecord.file_name}`, pageWidth - 30, 55, { align: 'right' });
    doc.text(`Run: ${run.run_name || fileRecord.run_id}`, pageWidth - 30, 70, { align: 'right' });
    doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, pageWidth - 30, 85, { align: 'right' });

    let currentY = 120;

    // --- Payroll Summary Section ---
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Batch Summary', 30, currentY);
    
    currentY += 15;
    
    autoTable(doc, {
      startY: currentY,
      margin: { left: 30, right: 30 },
      theme: 'grid',
      head: [],
      body: [
        ['Total Employees', linkedPayments.length.toString(), 'Total Amount', fmtINR(totalAmount)],
        ['File Type', fileRecord.file_type || 'N/A', 'Sender Account', fileRecord.sender_account || 'N/A'],
        ['Bank File Status', fileRecord.file_status || 'N/A', 'Currency', 'INR'],
        ['Method Breakdown', methodBreakdown, 'Ack Ref', 'N/A']
      ],
      bodyStyles: { textColor: [51, 65, 85], fontSize: 10, cellPadding: 6 },
      columnStyles: {
        0: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: 100 },
        2: { fontStyle: 'bold', fillColor: [248, 250, 252], cellWidth: 100 }
      },
      styles: { lineColor: [226, 232, 240] }
    });

    currentY = doc.lastAutoTable.finalY + 30;

    // --- Employee Payment Details Table ---
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Payment Details', 30, currentY);
    
    currentY += 15;

    autoTable(doc, {
      startY: currentY,
      margin: { left: 30, right: 30 },
      theme: 'striped',
      head: [['Sr.', 'Employee', 'Emp ID', 'Customer ID', 'Bank Account', 'IFSC', 'Method', 'Net Pay', 'Status', 'Ref']],
      body: tableBody,
      headStyles: { fillColor: [30, 64, 175], textColor: 255, fontSize: 9, fontStyle: 'bold' },
      bodyStyles: { textColor: [51, 65, 85], fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 25 },
        7: { halign: 'right', fontStyle: 'bold', textColor: [22, 163, 74] } // Green amount
      },
      didParseCell: function(data) {
        if (data.section === 'body' && data.column.index === 8) {
           const status = data.cell.raw.toString().toLowerCase();
           if (status === 'paid' || status === 'success') {
             data.cell.styles.textColor = [22, 163, 74]; // Green
             data.cell.styles.fontStyle = 'bold';
           } else if (status === 'failed' || status === 'error') {
             data.cell.styles.textColor = [220, 38, 38]; // Red
             data.cell.styles.fontStyle = 'bold';
           } else {
             data.cell.styles.textColor = [202, 138, 4]; // Yellow/Orange
           }
        }
      }
    });

    currentY = doc.lastAutoTable.finalY + 20;

    // --- Payment Totals Section ---
    autoTable(doc, {
      startY: currentY,
      margin: { left: 30, right: 30 },
      theme: 'grid',
      head: [['Total Paid', 'Total Amount', 'Pending Payments', 'Failed Payments']],
      body: [[
        linkedPayments.length.toString(),
        fmtINR(totalAmount),
        totalPending.toString(),
        totalFailed.toString()
      ]],
      headStyles: { fillColor: [226, 232, 240], textColor: [15, 23, 42], fontSize: 10, fontStyle: 'bold' },
      bodyStyles: { textColor: [15, 23, 42], fontSize: 11, fontStyle: 'bold', halign: 'center' }
    });

    // --- Footer Section ---
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const footerY = doc.internal.pageSize.height - 30;
      
      doc.setDrawColor(226, 232, 240);
      doc.line(30, footerY - 10, pageWidth - 30, footerY - 10);
      
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.setFont('helvetica', 'italic');
      doc.text('Confidentiality Note: This document contains confidential payroll and banking information intended for authorized personnel only.', 30, footerY);
      
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated by Payroll HRMS on ${new Date().toLocaleString('en-IN')}`, 30, footerY + 15);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, footerY + 15, { align: 'right' });
    }

    // Trigger Download
    const runCode = (run.run_name || fileRecord.run_id || 'RUN').replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
    const dateStr = new Date().toLocaleDateString('en-GB').replace(/\//g, '');
    const filename = `PAYSLIP_DATA_${runCode}_${dateStr}.pdf`;
    
    doc.save(filename);

  } catch (error) {
    console.error('PDF Generation Error:', error);
    throw error;
  }
};
